/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, DeleteSubtenantResponse, Subtenant } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type DeleteSubtenantMutationConfig = {
  onSuccess?: (data: void, variables: Subtenant) => void;
  onError?: (error: unknown, variables: Subtenant) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: Subtenant) => Promise<unknown> | void;
};

/**
 * Hook for deleting subtenant by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful subtenant deletion
 * @param config.onError - Called if subtenant deletion fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for deleting subtenant
 *
 * @example
 * ```tsx
 * const { mutate } = useDeleteSubtenantMutation({
 *   onSuccess: (data) => console.log('Subtenant Deleted:', data),
 *   onError: (error) => console.error('Subtenant Deletion failed:', error)
 * });
 *
 * // Delete Subtenant
 * mutate({ subtenantId: '9fb763f7-33de-4969-a7a0-b808f4637318' });
 * ```
 */

export function useDeleteSubtenantMutation({ onSuccess, onError, onSettled, onMutate }: DeleteSubtenantMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<DeleteSubtenantResponse, ApiError, Subtenant>({
    mutationFn: (subtenant: Subtenant) => {
      return mgmtPlaneAPIClient.subtenants.deleteSubtenant({ tenantId: tenantId!, id: subtenant.id });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error };
}
